// Where things live in the customer workspace: the one table behind the
// sidebar, the top bar (a hub's tabs, or the crumbs of a page under one)
// and the ⌘K jump palette, so the three always use the same names and agree
// on which row a URL belongs to.
//
//   (no label)  Home
//   Work        Meetings, Todos, Tasks, Projects
//   Team        Agents, Subagents, Automations, Skills
//   Workspace   Vault, Code, Settings
//
// A row that covers several pages is a HUB. Every page keeps its own URL
// and its own name (the palette finds "Todos", "Org chart", "Runs" …); while
// you are on one of them, the top bar shows the hub's pages as tabs:
//
//   Projects      Projects · Deliverables
//   Agents        Agents · Org chart
//   Automations   Workflows · Runs (/workflows?tab=activity) · Schedules · Deployments
//
// A hub lists only the pages switched on for the workspace: with one left,
// the row is that page's own (its name, icon and URL, no tabs); with none,
// it is hidden. The row opens its namesake page when that is on, else the
// first of its pages that is on.
//
// Todos and Tasks are two rows, not one hub: a "Tasks" row with a Todos tab
// opened Todos and hid Todos behind Tasks (operator, 20 Oct 2026).
//
// Recording a meeting is an action (the button above the nav), not a
// destination. On /record that button is the current page and no nav row
// is (Meetings stays plain); the breadcrumb still reads "Meetings › Record".

import {
  CalendarClock,
  ClipboardList,
  Code2,
  FolderKanban,
  GitBranch,
  LayoutDashboard,
  Lightbulb,
  ListChecks,
  ListTree,
  Lock,
  Network,
  Package,
  Rocket,
  Settings,
  Timer,
  UsersRound,
  Workflow,
  type LucideIcon,
} from "lucide-react";

import type { CustomerSettingsFlag } from "@/api/customerSettings";
import { attentionTone, type AttentionTone } from "@/features/attention/tones";
import { WORKSPACE_SECTIONS } from "@/features/agents/sectionMeta";
import { storeSections, type FileStoreKind, type SectionKey } from "@/features/settings/sections";
import { pluralize } from "@/lib/format";

/** A number the sidebar shows beside a row (see useNavCounts): "todos" the
 *  Todos row's (due), "tasks" the Tasks row's (tasks that need you). */
export type NavCountKey = "meetings" | "todos" | "tasks" | "skills" | "code";

/**
 * The Tasks row's count: todos due today or earlier (amber when any are
 * late) plus tasks waiting for you (an OK, or an answer not delivered).
 * The label says both parts: "3 todos due, 1 overdue; 1 task needs you".
 * null when nothing waits. `todos` is null with Todos off; `tasks` with
 * Tasks off.
 */
export function tasksRowCount(
  todos: { due: number; overdue: number } | null,
  tasks: number | null,
): { value: number; tone: AttentionTone; label: string } | null {
  const due = todos?.due ?? 0;
  const overdue = todos?.overdue ?? 0;
  const need = tasks ?? 0;
  if (due + need <= 0) return null;
  const parts = [
    due > 0 ? (overdue > 0 ? `${pluralize(due, "todo")} due, ${overdue} overdue` : `${pluralize(due, "todo")} due today`) : "",
    need > 0 ? `${pluralize(need, "task")} ${need === 1 ? "needs" : "need"} you` : "",
  ].filter(Boolean);
  return { value: due + need, tone: due > 0 ? attentionTone("todos", { overdue }) : attentionTone("tasks"), label: parts.join("; ") };
}

/** One destination: a page with its own URL and its own name. */
export interface NavPage {
  key: string;
  /** Where it opens. A page told apart by its query carries it (Runs). */
  to: string;
  /** Its own name: in the palette, as a crumb, and as the row when it stands alone. */
  label: string;
  /** Its name as one of a hub's tabs, where that differs ("Your todos"). */
  tab?: string;
  icon: LucideIcon;
  /** URL prefixes that belong to it, the pages under it included. */
  match: string[];
  /** Hide unless this customer-settings flag is true. The route guards
   *  itself too — hiding a nav item is presentation, not access. */
  flag?: NavFlag;
  /** "within": every URL it matches shows the hub's tabs (the agent
   *  workspace, where an agent is a selection, not a page under Agents).
   *  Otherwise only its own path does; the pages under it show crumbs. */
  tabsOn?: "within";
  /** Extra words the palette matches on. */
  keywords?: string;
}

/** A sidebar row: one page, or a hub of several. */
export interface NavItem {
  key: string;
  /** The hub's name; a one-page row has the page's. */
  label: string;
  icon: LucideIcon;
  /** In tab order. */
  pages: NavPage[];
  count?: NavCountKey;
}

/** A switch a page is gated on: a customer-settings flag, or `deployments`
 *  (Deployments, read from the workflows capacity route, not the settings:
 *  useNavFlags adds it when it knows it; absent = the page stays hidden). */
export type NavFlag = CustomerSettingsFlag | "deployments";

/** The workspace's flags as far as they are known (customer settings, or
 *  nothing while they load). */
export type NavFlags = Partial<Record<NavFlag, boolean>> | undefined;

/** The nav's switches with Deployments as the capacity route says it (`deployments`; unknown or off: left out, so the page stays hidden). useNavFlags merges it in. */
export function withDeploymentsFlag(flags: NavFlags, deployments: boolean | undefined): NavFlags {
  if (deployments !== true) return flags;
  return { ...(flags ?? {}), deployments: true };
}

/** Whether a page is on. `=== true` so a still-loading settings response
 *  never flashes a gated page in. */
export function navPageVisible(page: NavPage, flags: NavFlags): boolean {
  return !page.flag || flags?.[page.flag] === true;
}

/** A row as it shows for one workspace. */
export interface NavRow {
  item: NavItem;
  key: string;
  label: string;
  icon: LucideIcon;
  /** Where the row goes: its namesake page when that is on ("Tasks" opens
   *  Tasks, not the Todos tab before it), else the first of its pages that is on. */
  to: string;
  /** Its pages that are on, in tab order: two or more are tabs in the top bar. */
  pages: NavPage[];
  /** URL prefixes that highlight it: those of its pages that are on. */
  match: string[];
  count?: NavCountKey;
}

/** How a row shows with these flags; null when none of its pages is on. */
export function navRow(item: NavItem, flags: NavFlags): NavRow | null {
  const pages = item.pages.filter((p) => navPageVisible(p, flags));
  if (pages.length === 0) return null;
  // A hub down to one page is that page's row: "Todos", "Schedules".
  const only = item.pages.length > 1 && pages.length === 1 ? pages[0] : null;
  return {
    item,
    key: item.key,
    label: only?.label ?? item.label,
    icon: only?.icon ?? item.icon,
    to: (pages.find((p) => p.key === item.key) ?? pages[0]).to,
    pages,
    match: pages.flatMap((p) => p.match),
    count: item.count,
  };
}

/** Whether a row shows at all. */
export function navItemVisible(item: NavItem, flags: NavFlags): boolean {
  return navRow(item, flags) !== null;
}

export interface NavGroup {
  /** Null = no heading (Home). */
  label: string | null;
  items: NavItem[];
}

const page = (p: NavPage, count?: NavCountKey): NavItem => ({ key: p.key, label: p.label, icon: p.icon, pages: [p], count });

export const NAV_GROUPS: NavGroup[] = [
  {
    label: null,
    items: [page({ key: "home", to: "/dashboard", label: "Home", icon: LayoutDashboard, match: ["/dashboard"] })],
  },
  {
    label: "Work",
    items: [
      page({ key: "meetings", to: "/meetings", label: "Meetings", icon: CalendarClock, match: ["/meetings"] }, "meetings"),
      // Your own todos, and work given to the agents and subagents
      // (docs/tasks): a row each, each gated per workspace (the pages guard
      // themselves too), each with its own count: todos due, tasks that need you.
      page({ key: "todos", to: "/todos", label: "Todos", icon: ListChecks, match: ["/todos"], flag: "todos_enabled", keywords: "to-do checklist" }, "todos"),
      page({ key: "tasks", to: "/tasks", label: "Tasks", icon: ClipboardList, match: ["/tasks"], flag: "tasks_enabled" }, "tasks"),
      {
        key: "projects",
        label: "Projects",
        icon: FolderKanban,
        pages: [
          { key: "projects", to: "/projects", label: "Projects", icon: FolderKanban, match: ["/projects"] },
          { key: "deliverables", to: "/deliverables", label: "Deliverables", icon: Package, match: ["/deliverables"] },
        ],
      },
    ],
  },
  {
    label: "Team",
    items: [
      {
        key: "agents",
        label: "Agents",
        icon: UsersRound,
        pages: [
          {
            key: "agents",
            to: "/agents",
            label: "Agents",
            icon: UsersRound,
            // The old per-surface URLs redirect into /agents/<t>/<section>;
            // matching them keeps the highlight during the redirect tick.
            match: ["/agents", "/chat", "/desktops", "/files", "/mail", "/routines", "/memories"],
            tabsOn: "within",
          },
          { key: "org", to: "/org", label: "Org chart", icon: Network, match: ["/org"], keywords: "team reporting lines" },
        ],
      },
      // Subagents (what the agents spawn) and every run. Its own row, apart
      // from Agents on purpose. Gated per workspace; the page itself still
      // serves subagents that exist.
      page({ key: "subagents", to: "/subagents", label: "Subagents", icon: Workflow, match: ["/subagents"], flag: "subagents_enabled" }),
      // What runs without being asked each time: workflows (pipelines of
      // subagent steps), their runs, and everything on a timetable. The
      // Workflows pages stay readable when switched off (old links); with
      // tasks off the agents' own Schedules tabs remain. (Handoffs is gone:
      // /handoffs redirects to /tasks; docs/tasks.)
      {
        key: "automations",
        label: "Automations",
        icon: GitBranch,
        pages: [
          { key: "workflows", to: "/workflows", label: "Workflows", icon: GitBranch, match: ["/workflows"], flag: "workflows_enabled", keywords: "pipeline" },
          {
            key: "runs",
            to: "/workflows?tab=activity",
            label: "Runs",
            icon: ListTree,
            match: ["/workflows/runs", "/workflows/jobs"],
            flag: "workflows_enabled",
            keywords: "activity history team task one-off jobs",
          },
          { key: "schedules", to: "/schedules", label: "Schedules", icon: Timer, match: ["/schedules"], flag: "tasks_enabled", keywords: "routines timetable recurring" },
          // Workflows as APIs (keys, docs, Try it), per workspace behind
          // `deployments_enabled`; the pages under it show crumbs.
          { key: "deployments", to: "/workflows/deployments", label: "Deployments", icon: Rocket, match: ["/workflows/deployments"], flag: "deployments", keywords: "api endpoint mcp keys deploy published" },
        ],
      },
      page({ key: "skills", to: "/skills", label: "Skills", icon: Lightbulb, match: ["/skills"] }, "skills"),
    ],
  },
  {
    label: "Workspace",
    items: [
      page({ key: "vault", to: "/shared", label: "Vault", icon: Lock, match: ["/shared"] }),
      page(
        {
          key: "code",
          to: "/code/sessions",
          label: "Code",
          icon: Code2,
          // /cc-sessions, /cc-deploys and /connections redirect into /code/*.
          match: ["/code", "/cc-sessions", "/cc-deploys", "/connections"],
        },
        "code",
      ),
      page({ key: "settings", to: "/settings", label: "Settings", icon: Settings, match: ["/settings"] }),
    ],
  },
];

/** Every sidebar row, top to bottom. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/** Every page, in sidebar and tab order. */
export const NAV_PAGES: NavPage[] = NAV_ITEMS.flatMap((i) => i.pages);

/** Every switch a page is gated on, on: the room a waiting first visit keeps. */
const EVERY_SWITCH_ON: NavFlags = Object.fromEntries(NAV_PAGES.flatMap((p) => (p.flag ? [[p.flag, true]] : [])));

/** A row as the sidebar draws it; `held` = its place kept, unseen. */
export interface DrawnRow {
  row: NavRow;
  held: boolean;
}

/** One group's rows as the sidebar draws them. While a first visit waits
 *  for the switches (`waiting`), a row that needs one keeps its place
 *  unseen and every other row draws at once; after that, each row shows
 *  as the switches say. */
export function drawnRows(items: NavItem[], flags: NavFlags, waiting: boolean): DrawnRow[] {
  const out: DrawnRow[] = [];
  for (const item of items) {
    const row = navRow(item, flags);
    if (row) {
      out.push({ row, held: false });
    } else if (waiting) {
      const room = navRow(item, EVERY_SWITCH_ON);
      if (room) out.push({ row: room, held: true });
    }
  }
  return out;
}

export function matchesPath(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

/** The row a URL belongs to, if any (whatever the workspace switches say). */
export function navItemFor(pathname: string): NavItem | undefined {
  return NAV_ITEMS.find((item) => item.pages.some((p) => p.match.some((m) => matchesPath(pathname, m))));
}

/** The page a URL belongs to, if any: the one with the longest matching prefix
 *  (/workflows/runs/<id> is under Runs, /workflows/<slug> under Workflows). */
export function navPageFor(pathname: string): NavPage | undefined {
  let best: NavPage | undefined;
  let length = -1;
  for (const p of NAV_PAGES) {
    for (const m of p.match) {
      if (matchesPath(pathname, m) && m.length > length) {
        best = p;
        length = m.length;
      }
    }
  }
  return best;
}

/** The page a hub tab is ON (not under) for this URL: its own path, or anything
 *  it matches for a `tabsOn: "within"` page. Pages on one path are told apart
 *  by their query (/workflows is Workflows, /workflows?tab=activity Runs). */
export function hubPageAt(pages: NavPage[], pathname: string, search: string): NavPage | undefined {
  const params = new URLSearchParams(search);
  const on = pages.filter((p) =>
    p.tabsOn === "within" ? p.match.some((m) => matchesPath(pathname, m)) : pathname === p.to.split("?")[0],
  );
  const queryOf = (p: NavPage) => [...new URLSearchParams(p.to.split("?")[1] ?? "")];
  return (
    on.find((p) => queryOf(p).length > 0 && queryOf(p).every(([k, v]) => params.get(k) === v)) ??
    on.find((p) => queryOf(p).length === 0)
  );
}

/** The hub whose tabs the top bar shows on this URL, and the current tab: only
 *  on one of the hub's own pages, and only while two or more of them are on. */
export function hubTabsAt(pathname: string, search: string, flags: NavFlags): { row: NavRow; current: NavPage } | null {
  const item = navItemFor(pathname);
  if (!item || item.pages.length < 2) return null;
  const row = navRow(item, flags);
  if (!row || row.pages.length < 2) return null;
  const current = hubPageAt(row.pages, pathname, search);
  return current ? { row, current } : null;
}

// ─── agent workspace sections ───

export interface AgentSectionDef {
  key: string;
  label: string;
  icon: LucideIcon;
  /** Words the palette also finds it by (an old name). */
  keywords?: string;
}

/** The agent workspace's tabs, in tab order (path segment → label): the
 *  Agents page's own list (features/agents/sectionMeta), with the old
 *  words the palette also finds them by. */
export const AGENT_SECTIONS: AgentSectionDef[] = WORKSPACE_SECTIONS.map((s) =>
  s.key === "routines" ? { ...s, keywords: "schedules schedule routines reminders plan now" } : s.key === "settings" ? { ...s, keywords: "configure" } : s,
);

/** Sections an agent has, the workspace's sectionsFor rule: Desktop only
 *  when its desktop is exposed, no Tasks or Skills for a family agent, no
 *  Mail where agents have no inbox of their own (`mail` false: Corvus
 *  storage, capabilities `agent_google`). Tasks is "Background tasks" on a
 *  workspace with Tasks off (`tasksOn` false). */
export function agentSectionsFor(
  hasDesktop: boolean,
  { family = false, tasksOn = true, mail = true }: { family?: boolean; tasksOn?: boolean; mail?: boolean } = {},
): AgentSectionDef[] {
  return AGENT_SECTIONS.filter(
    (s) => (s.key !== "desktop" || hasDesktop) && !(family && (s.key === "tasks" || s.key === "skills")) && (mail || s.key !== "mail"),
  ).map((s) =>
    s.key === "tasks" && !tasksOn ? { ...s, label: "Background tasks" } : s,
  );
}

export function agentSectionLabel(key: string | undefined): string | null {
  return AGENT_SECTIONS.find((s) => s.key === key)?.label ?? null;
}

// ─── Code sub-pages ───
//
// Same names as the Code page's tabs. Breadcrumbs show a second level only
// where it is a PATH (/agents/<t>/<section>, /code/<section>); tabs kept in
// the query string (Skills, Vault) or the hash (Settings) are not crumbs.

export const CODE_SECTIONS: { key: string; to: string; label: string }[] = [
  { key: "sessions", to: "/code/sessions", label: "Coding sessions" },
  { key: "connections", to: "/code/connections", label: "Connected sessions" },
  // Apps (apps spec §10.6): agent-built apps, "Apps" in every workspace, so
  // "Deployments" means one thing (workflows as APIs); /code/deployments redirects here.
  { key: "apps", to: "/code/apps", label: "Apps" },
];

/** A Code section's name: where Connections is on, the paired coding sessions are "Paired sessions", so "connection" means one thing. */
export function codeSectionLabel(key: string, label: string, connections: boolean): string {
  return connections && key === "connections" ? "Paired sessions" : label;
}

// ─── Settings sections (the Settings page keeps its section in the URL hash) ───
//
// Built from SECTIONS in features/settings/sections.ts (same keys, labels,
// order and flags as the Settings nav), plus the extra words the palette
// matches on.

const SETTINGS_KEYWORDS: Partial<Record<SectionKey, string>> = {
  general: "workspace family agents subagents market data",
  tasks: "who does it limits learned give work",
  outside: "reachable public links keys apps take down published",
  mail: "email inbox reply instructions",
  linked: "email mail calendar outlook gmail microsoft google inbox link",
  telegram: "allowlist bot message",
  whatsapp: "phone number groups",
  models: "ai model inference provider",
  connections: "connect gitlab motion apis mcp keys integrations",
  integrations: "tools apis mcp servers keys",
  machines: "machine server ssh pair computer laptop commands shell",
  styles: "documents brand logo format",
  voices: "voice clone speech audio podcast tts",
  media: "youtube video sign in cookies",
  signatures: "sign initials pen",
  appearance: "theme glass earth midnight dark look",
  devices: "phone iphone app signed in sign out",
};

export interface SettingsJumpSection {
  key: string;
  label: string;
  keywords?: string;
  flag?: CustomerSettingsFlag;
  offFlag?: CustomerSettingsFlag;
  anyFlag?: readonly CustomerSettingsFlag[];
}

/** The palette's Settings sections for a workspace's storage: on Corvus
 *  storage Linked account in Mail's place (sections.storeSections). */
export function settingsSectionsFor(store: FileStoreKind = "drive"): SettingsJumpSection[] {
  return storeSections(store).map((s) => ({
    key: s.key,
    label: s.label,
    keywords: SETTINGS_KEYWORDS[s.key],
    flag: s.flag,
    offFlag: s.offFlag,
    anyFlag: s.anyFlag,
  }));
}

/** Google Drive's (every workspace before Corvus storage): exactly SECTIONS. */
export const SETTINGS_SECTIONS: SettingsJumpSection[] = settingsSectionsFor("drive");
